import { makeAutoObservable } from "mobx";
import type { CategoriesApi } from "@/api/categories";
import { CategoryMap } from "@/core/autolinking";
import { browserStorage, type CacheStorage, readListCache, writeListCache } from "./listCache";
import { PagedResource, Resource } from "./Resource";

type GroupedCategory = {
    category: Category;
    count: number;
};

/** The API pages episodes 30 at a time */
const EPISODES_PAGE_SIZE = 30;

const LIST_CACHE_KEY = "hp_categories_v2";
const LIST_CACHE_MAX_AGE = 1000 * 60 * 60 * 12; // 12 hours

/**
 * The category overview and the category pages.
 *
 * Server data lives in resources, keyed by the category slug where it belongs to one category, so
 * asking for another category drops the old one's data at once and a slow response for the previous
 * category can never overwrite the new one.
 */
export class CategoryStore {
    /** Every category the API has, as it came. `categories` is the browsable subset. */
    readonly list: Resource<Category[]>;
    readonly detail: Resource<Category, string>;
    readonly feed: PagedResource<Episode, string>;
    readonly toplistPodcasts: Resource<Podcast[], string>;

    private thumbs = new Map<number, Promise<Podcast[]>>();

    constructor(
        private readonly api: CategoriesApi,
        private readonly storage: CacheStorage | null = browserStorage()
    ) {
        this.list = new Resource(() => this.fetchList(), {
            initial: readListCache<Category[]>(this.storage, LIST_CACHE_KEY, LIST_CACHE_MAX_AGE),
        });
        this.detail = new Resource((slug) => this.idFor(slug).then((id) => this.api.get(id)));
        this.feed = new PagedResource(
            (slug, page) => this.idFor(slug).then((id) => this.api.episodes(id, page)),
            EPISODES_PAGE_SIZE
        );
        this.toplistPodcasts = new Resource((slug) => this.idFor(slug).then((id) => this.api.toplist(id)));

        makeAutoObservable<CategoryStore, "api" | "storage" | "thumbs">(this, {
            api: false,
            storage: false,
            thumbs: false,
            list: false,
            detail: false,
            feed: false,
            toplistPodcasts: false,
        });
    }

    /** Starts loading the category list in the background, e.g. at startup */
    preload() {
        return this.list.load();
    }

    /** What the overview and the autolinking show: no catch-all "podcaster", no empty ones, biggest first */
    get categories(): Category[] {
        return (this.list.data ?? [])
            .filter((c) => c.name.toLowerCase() !== "podcaster")
            .filter((c) => c.podcasts_count && c.podcasts_count > 0)
            .sort((a, b) => (b.podcasts_count || 0) - (a.podcasts_count || 0));
    }

    get activeCategory(): Category | null {
        return this.detail.data ?? null;
    }

    get podcasts(): Podcast[] {
        return this.activeCategory?.podcasts ?? [];
    }

    get latest(): Episode[] {
        return this.feed.items;
    }

    get toplist(): Podcast[] {
        return this.toplistPodcasts.data ?? [];
    }

    /** True while another page of episodes is being fetched */
    get loading() {
        return this.feed.loading;
    }

    /** True once the category, its episodes and its toplist have all arrived for `slug` */
    isOpen(slug: string) {
        return [this.detail, this.feed, this.toplistPodcasts].every((r) => r.isFor(slug) && r.loaded);
    }

    /**
     * Loads a category together with its first page of episodes and its toplist. The promise settles when
     * all of it has arrived, so a page that suspends on it never shows half a category. Failures end up in
     * the resources' `error`, not in a rejection: an unknown slug just leaves `activeCategory` empty.
     */
    async open(slug: string) {
        await Promise.all([this.detail.load(slug), this.feed.load(slug), this.toplistPodcasts.load(slug)]);
    }

    loadMore() {
        return this.feed.loadMore();
    }

    /** The top podcasts of a category, for its tile on the overview. Cached per category. */
    topPodcasts(categoryId: number): Promise<Podcast[]> {
        let pending = this.thumbs.get(categoryId);
        if (!pending) {
            pending = this.api.toplist(categoryId);
            // A failed request must not stick, so the next tile that asks retries
            pending.catch(() => this.thumbs.delete(categoryId));
            this.thumbs.set(categoryId, pending);
        }
        return pending;
    }

    get similarCategories(): Category[] {
        const grouped: GroupedCategory[] = [];
        const active = this.activeCategory;
        if (!active) {
            return [];
        }
        this.podcasts.forEach((p: Podcast) => {
            p.categories.forEach((c: Category) => {
                if (c.slug === active.slug || c.slug === "podcaster") {
                    return;
                }
                const found = grouped.find((g) => g.category.id === c.id);
                if (found) {
                    found.count += 1;
                } else {
                    grouped.push({ category: c, count: 1 });
                }
            });
        });

        grouped.sort((a, b) => (a.count > b.count ? -1 : 1));

        return grouped.map((g) => g.category).splice(0, 15);
    }

    // Category name -> link, for autolinking text that mentions a category
    get categoryMap(): CategoryMap {
        const map: CategoryMap = {};
        this.categories.forEach((c) => {
            map[c.name.replace(/[^a-zA-ZåäöÅÄÖ0-9 ]/g, "")] = `/kategori/${c.slug}`;
        });
        return map;
    }

    private async idFor(slug: string): Promise<number> {
        await this.list.load();
        const category = this.list.data?.find((c) => c.slug === slug);
        if (!category) {
            throw new Error(`Category not found: ${slug}`);
        }
        return category.id;
    }

    private async fetchList(): Promise<Category[]> {
        const categories = await this.api.list();
        writeListCache(this.storage, LIST_CACHE_KEY, categories);
        return categories;
    }
}
